import {
	GetEshopDetailQuery,
	useGetAllEshopsSlugsQuery,
	useGetEshopDetailQuery,
} from '@/api/sanity/generated';
import { WithRequiredPropertyAndNotNullable } from '@/types/withRequiredProperty';
import { serialize } from 'next-mdx-remote/serialize';
import type { MDXRemoteSerializeResult } from 'next-mdx-remote';
import { Maybe } from '@/utils/types';
import { sanityLanguage } from '@/constants/language';
import { createCache } from '@/utils/cache';

export type Eshop = WithRequiredPropertyAndNotNullable<
	Omit<
		Omit<NonNullable<GetEshopDetailQuery>['allEshops'][number], '__typename'>,
		'contentArea1' | 'contentArea2' | 'contentArea3' | 'contentArea4' | 'contentArea5'
	> & {
		contentArea1: {
			title: string;
			content: MDXRemoteSerializeResult;
		};
		contentArea2: {
			title: string;
			content: MDXRemoteSerializeResult;
		};
		contentArea3: {
			title: string;
			content: MDXRemoteSerializeResult;
		};
		contentArea4: {
			title: string;
			content: MDXRemoteSerializeResult;
		};
		contentArea5: {
			title: string;
			content: MDXRemoteSerializeResult;
		};
	},
	'merchantLink' | 'eshopApiId'
>;

export async function getEshopsSlugs(): Promise<string[]> {
	let skip = 0;
	const maxSkip = 200;
	const result: string[] = [];

	while (true) {
		if (skip > maxSkip) {
			break;
		}

		const eshops = await useGetAllEshopsSlugsQuery.fetcher({
			offset: skip,
			i18nLang: sanityLanguage,
		})();

		if (!eshops?.allEshops?.length) {
			break;
		}

		if (eshops.allEshops) {
			const eshopsList = eshops.allEshops
				.map((eshop) => eshop.slug?.current?.toLowerCase())
				.filter(Boolean);

			result.push(...eshopsList);
		}

		skip += eshops.allEshops.length;
	}

	return result;
}

const cache = createCache<Eshop, string>(20000, 'discounts-eshops');

export async function getEshopDetail(slug: string): Promise<Eshop> {
	const cached = cache.get(slug);

	if (cached) {
		return new Promise((resolve) => resolve(cached));
	}

	const eshops = await useGetEshopDetailQuery.fetcher({
		slug,
		i18nLang: sanityLanguage,
	})();

	if (!eshops?.allEshops?.length) {
		throw new Error(`Eshop with slug ${slug} not found`);
	}

	const eshop = eshops.allEshops[0];

	if (!eshop) {
		throw new Error(`Eshop with slug ${slug} not found`);
	}

	const data = {
		...eshop,
		contentArea1: {
			title: getPageTitle(eshop.contentArea1),
			content: await serialize(eshop.contentArea1 || ''),
		},
		contentArea2: {
			title: getPageTitle(eshop.contentArea2),
			content: await serialize(eshop.contentArea2 || ''),
		},
		contentArea3: {
			title: getPageTitle(eshop.contentArea3),
			content: await serialize(eshop.contentArea3 || ''),
		},
		contentArea4: {
			title: getPageTitle(eshop.contentArea4),
			content: await serialize(eshop.contentArea4 || ''),
		},
		contentArea5: {
			title: getPageTitle(eshop.contentArea5),
			content: await serialize(eshop.contentArea5 || ''),
		},
		merchantLink: eshop.merchantLink || '',
		eshopApiId: eshop.eshopApiId || '',
	};

	cache.set(slug, data, 604800000); // Cache for 1 week

	return data;
}

function getPageTitle(markdown: Maybe<string>): string {
	if (!markdown) {
		return '';
	}

	const matches = markdown.match(/##\s(.*)/g);

	return matches
		? matches.map((match) => match.replace('## ', '').replace(/\*\*/g, '')).join()
		: '';
}
